// Modèles 3D des armes, communs à l'arme tenue en main (actors.ts) et aux icônes (icons.ts).
// Les armes sont fabriquées ici en low-poly (comme le décor, voir lowpoly.ts) : canon vers +X, dimensions réelles en mètres,
// puis recentrées. file : clé de cache (ou fichier dans assets/models/weapons pour un modèle importé) ; len : longueur réelle (m) ;
// yaw : rotation (radians, autour de la verticale) qui pointe le canon vers +Z (l'avant du personnage) ;
// drop : pièces à retirer d'un modèle importé ; at : points de fixation des accessoires (repère de construction, avant recentrage).
import * as THREE from 'three';
import { MODS, modFits, type ModSlot, type Mods, type WeaponId } from '../shared/weapons';
import { flat } from './lowpoly';

// Chargeur : point d'attache, inclinaison, longueur, largeur, épaisseur. base : chargeur dans la poignée (pistolet)
interface MagAt { x: number; y: number; angle: number; len: number; w: number; d: number; base?: boolean }
interface Anchors { optic?: [number, number]; muzzle?: [number, number]; mag?: MagAt; grip?: [number, number] }
export interface WeaponModel { file: string; len: number; yaw: number; drop?: RegExp; build?: () => THREE.Group; at?: Anchors }

// Teintes : acier bruni, polymère noir, bois, garnitures olive
const STEEL = 0x555a5f, POLY = 0x3b3e41, WOOD = 0x8a5c38, WOOD_DARK = 0x684429, OLIVE = 0x5a6339, BRIGHT = 0x8c9196, TAN = 0x9a8460;

class Gun {
  g = new THREE.Group();
  private last: THREE.Object3D | null = null;
  // Boîte : x = début (côté crosse), longueur vers +X ; y, z = centre
  box(x0: number, len: number, h: number, w: number, color: number, y = 0, tiltZ = 0) {
    const m = new THREE.Mesh(new THREE.BoxGeometry(len, h, w), flat(color, { rough: 0.6, metal: color === STEEL || color === BRIGHT ? 0.4 : 0 }));
    m.position.set(x0 + len / 2, y, 0);
    m.rotation.z = tiltZ;
    return this.put(m);
  }
  // Tube le long de X
  tube(x0: number, len: number, r: number, color: number, y = 0, seg = 8) {
    const m = new THREE.Mesh(new THREE.CylinderGeometry(r, r, len, seg), flat(color, { rough: 0.5, metal: 0.4 }));
    m.rotation.z = Math.PI / 2;
    m.position.set(x0 + len / 2, y, 0);
    return this.put(m);
  }
  // Pièce inclinée posée sous l'arme (poignée, chargeur) : point d'attache en haut
  hang(x: number, y: number, len: number, w: number, d: number, color: number, angle: number) {
    const m = new THREE.Mesh(new THREE.BoxGeometry(w, len, d), flat(color, { rough: 0.7 }));
    m.geometry.translate(0, -len / 2, 0);
    m.position.set(x, y, 0);
    m.rotation.z = angle;
    return this.put(m);
  }
  // Nomme la dernière pièce (le chargeur d'origine, caché quand un chargeur accessoire est monté)
  as(name: string) { if (this.last) this.last.name = name; return this; }
  private put(m: THREE.Object3D) { m.castShadow = true; this.g.add(m); this.last = m; return this; }
  done() {
    // Recentrage : l'arme tenue est placée par son centre (comme les modèles importés) ; le décalage sert aux accessoires
    const c = new THREE.Box3().setFromObject(this.g).getCenter(new THREE.Vector3());
    for (const ch of this.g.children) ch.position.sub(c);
    this.g.userData.center = c;
    return this.g;
  }
}

// Fusil d'assaut 5,56 (silhouette type M4) : crosse télescopique, garde-main à rail, chargeur courbe
function assaultRifle() {
  const k = new Gun();
  k.box(0, 0.06, 0.1, 0.045, POLY, -0.02)            // plaque de crosse
    .box(0.06, 0.16, 0.06, 0.04, POLY, -0.005)       // crosse
    .tube(0.04, 0.2, 0.012, STEEL, 0.03, 6)          // tube de crosse
    .box(0.22, 0.2, 0.07, 0.05, STEEL, 0.015)        // boîte de culasse
    .box(0.24, 0.16, 0.018, 0.03, STEEL, 0.06)       // rail supérieur
    .box(0.27, 0.05, 0.035, 0.012, POLY, 0.088)      // hausse
    .box(0.42, 0.24, 0.062, 0.055, POLY, 0.018)      // garde-main
    .box(0.42, 0.24, 0.012, 0.03, STEEL, 0.054)      // rail du garde-main
    .tube(0.66, 0.15, 0.011, STEEL, 0.02)            // canon
    .tube(0.79, 0.06, 0.016, POLY, 0.02, 6)          // cache-flamme
    .box(0.6, 0.025, 0.07, 0.012, STEEL, 0.07)       // guidon
    .hang(0.255, -0.01, 0.1, 0.035, 0.03, POLY, -0.3) // poignée
    .hang(0.35, -0.015, 0.14, 0.055, 0.024, STEEL, 0.18).as('mag'); // chargeur courbe (incliné vers l'avant)
  return k.done();
}

// Fusil à lunette 7,62 : crosse et fût en bois, canon long, lunette, levier de culasse
function scopedRifle() {
  const k = new Gun();
  k.box(0, 0.08, 0.13, 0.045, WOOD_DARK, -0.025)     // talon
    .box(0.08, 0.26, 0.1, 0.042, WOOD, -0.012, 0.06) // crosse
    .box(0.33, 0.42, 0.055, 0.044, WOOD, 0.0)        // fût
    .box(0.3, 0.25, 0.05, 0.035, STEEL, 0.035)       // boîte de culasse
    .tube(0.72, 0.4, 0.011, STEEL, 0.03)             // canon
    .tube(0.33, 0.24, 0.02, POLY, 0.105, 10)         // lunette
    .tube(0.31, 0.05, 0.027, POLY, 0.105, 10)        // oculaire
    .tube(0.53, 0.06, 0.03, POLY, 0.105, 10)         // objectif
    .box(0.38, 0.03, 0.05, 0.015, STEEL, 0.07)       // montage arrière
    .box(0.5, 0.03, 0.05, 0.015, STEEL, 0.07)        // montage avant
    .box(0.36, 0.015, 0.012, 0.08, BRIGHT, 0.04)     // levier de culasse (dépasse sur le côté)
    .hang(0.33, -0.02, 0.06, 0.04, 0.02, STEEL, 0)   // pontet
    .hang(0.29, -0.01, 0.08, 0.038, 0.03, WOOD_DARK, -0.35) // poignée
    .hang(0.4, -0.025, 0.06, 0.05, 0.026, STEEL, 0).as('mag'); // chargeur court
  return k.done();
}

// Fusil à pompe : crosse et pompe en bois, tube magasin sous le canon
function shotgun() {
  const k = new Gun();
  k.box(0, 0.07, 0.12, 0.045, POLY, -0.025)
    .box(0.07, 0.26, 0.09, 0.042, WOOD, -0.012, 0.07)
    .box(0.33, 0.2, 0.075, 0.048, STEEL, 0.01)       // boîte de culasse
    .tube(0.53, 0.47, 0.014, STEEL, 0.035)           // canon
    .tube(0.53, 0.4, 0.013, STEEL, -0.0, 8)          // tube magasin
    .box(0.6, 0.18, 0.045, 0.05, WOOD_DARK, 0.0)     // pompe
    .box(0.98, 0.012, 0.012, 0.01, BRIGHT, 0.054)    // guidon
    .hang(0.36, -0.02, 0.07, 0.045, 0.02, STEEL, 0)  // pontet
    .hang(0.33, -0.01, 0.07, 0.036, 0.03, WOOD, -0.45);
  return k.done();
}

// Pistolet 9 mm : glissière, carcasse, poignée inclinée
function pistol() {
  const k = new Gun();
  k.box(0, 0.19, 0.032, 0.028, STEEL, 0.04)          // glissière
    .box(0.02, 0.15, 0.02, 0.026, POLY, 0.016)       // carcasse
    .box(0.17, 0.025, 0.012, 0.012, STEEL, 0.034)    // bout du canon
    .box(0.005, 0.012, 0.012, 0.01, POLY, 0.062)     // hausse
    .box(0.17, 0.01, 0.01, 0.006, POLY, 0.06)        // guidon
    .hang(0.045, 0.008, 0.11, 0.034, 0.03, POLY, -0.32) // poignée
    .hang(0.085, 0.005, 0.035, 0.012, 0.006, POLY, 0) // queue de détente
    .box(0.065, 0.06, 0.006, 0.026, POLY, -0.022);   // pontet
  return k.done();
}

// Pistolet-mitrailleur 9 mm : crosse repliable, boîtier court, chargeur droit
function smg() {
  const k = new Gun();
  k.box(0, 0.02, 0.07, 0.04, POLY, -0.005)           // plaque de crosse
    .box(0.02, 0.1, 0.012, 0.012, STEEL, 0.02)       // tige de crosse
    .box(0.02, 0.1, 0.012, 0.012, STEEL, -0.02)
    .box(0.12, 0.26, 0.06, 0.045, POLY, 0.02)        // boîtier
    .box(0.14, 0.22, 0.012, 0.028, STEEL, 0.056)     // rail supérieur
    .box(0.38, 0.12, 0.045, 0.04, POLY, 0.022)       // manchon
    .tube(0.5, 0.06, 0.009, STEEL, 0.025)            // canon
    .box(0.44, 0.02, 0.03, 0.01, STEEL, 0.06)        // guidon
    .hang(0.17, -0.01, 0.09, 0.034, 0.03, POLY, -0.3) // poignée
    .hang(0.27, -0.01, 0.16, 0.03, 0.022, STEEL, 0.05).as('mag'); // chargeur droit
  return k.done();
}

// Fusil d'assaut 7,62 : crosse et garde-main en bois, boîtier en acier, chargeur très courbé
function akRifle() {
  const k = new Gun();
  k.box(0, 0.05, 0.11, 0.042, WOOD_DARK, -0.03)       // talon
    .box(0.05, 0.2, 0.08, 0.04, WOOD, -0.012, 0.1)    // crosse
    .box(0.25, 0.25, 0.07, 0.048, STEEL, 0.015)       // boîtier
    .box(0.26, 0.23, 0.02, 0.044, BRIGHT, 0.058)      // couvercle
    .box(0.5, 0.18, 0.055, 0.05, WOOD, 0.012)         // garde-main
    .tube(0.5, 0.2, 0.01, STEEL, 0.052, 6)            // tube d'emprunt de gaz
    .tube(0.68, 0.2, 0.011, STEEL, 0.022)             // canon
    .box(0.82, 0.03, 0.05, 0.012, STEEL, 0.05)        // guidon
    .box(0.36, 0.015, 0.012, 0.06, BRIGHT, 0.03)      // levier d'armement
    .hang(0.3, -0.02, 0.09, 0.035, 0.03, WOOD_DARK, -0.3) // poignée
    .hang(0.41, -0.02, 0.17, 0.055, 0.026, POLY, 0.38).as('mag'); // chargeur « banane »
  return k.done();
}

// Fusil à verrou 7,62 : long canon, crosse en bois clair, levier de culasse, sans lunette (viseur en option)
function boltRifle() {
  const k = new Gun();
  k.box(0, 0.06, 0.13, 0.045, WOOD_DARK, -0.028)      // talon
    .box(0.06, 0.3, 0.09, 0.042, TAN, -0.01, 0.07)    // crosse
    .box(0.36, 0.48, 0.05, 0.044, TAN, 0.0)           // fût
    .box(0.33, 0.22, 0.045, 0.034, STEEL, 0.034)      // boîte de culasse
    .tube(0.84, 0.32, 0.011, STEEL, 0.03)             // canon
    .box(0.38, 0.012, 0.012, 0.09, BRIGHT, 0.04)      // levier de culasse
    .box(0.35, 0.012, 0.03, 0.012, STEEL, 0.065)      // hausse
    .box(1.12, 0.015, 0.03, 0.01, STEEL, 0.055)       // guidon
    .hang(0.36, -0.02, 0.06, 0.04, 0.02, STEEL, 0)    // pontet
    .hang(0.31, -0.01, 0.08, 0.038, 0.03, WOOD_DARK, -0.35);
  return k.done();
}

export const WEAPON_MODELS: Record<WeaponId, WeaponModel> = {
  ar: { file: 'lowpoly:ar', len: 0.85, yaw: -Math.PI / 2, build: assaultRifle,
    at: { optic: [0.33, 0.069], muzzle: [0.85, 0.02], mag: { x: 0.35, y: -0.015, angle: 0.18, len: 0.14, w: 0.055, d: 0.024 }, grip: [0.55, -0.013] } },
  dmr: { file: 'lowpoly:dmr', len: 1.12, yaw: -Math.PI / 2, build: scopedRifle,
    at: { muzzle: [1.12, 0.03], mag: { x: 0.4, y: -0.025, angle: 0, len: 0.06, w: 0.05, d: 0.026 }, grip: [0.62, -0.028] } },
  sg: { file: 'lowpoly:sg', len: 1.0, yaw: -Math.PI / 2, build: shotgun, at: { optic: [0.43, 0.048], grip: [0.7, -0.023] } },
  pistol: { file: 'lowpoly:pistol', len: 0.22, yaw: -Math.PI / 2, build: pistol,
    at: { optic: [0.07, 0.056], muzzle: [0.195, 0.034], mag: { x: 0.0104, y: -0.096, angle: -0.32, len: 0.03, w: 0.034, d: 0.03, base: true } } },
  smg: { file: 'lowpoly:smg', len: 0.56, yaw: -Math.PI / 2, build: smg,
    at: { optic: [0.25, 0.062], muzzle: [0.56, 0.025], mag: { x: 0.27, y: -0.01, angle: 0.05, len: 0.16, w: 0.03, d: 0.022 }, grip: [0.44, 0.0] } },
  ak: { file: 'lowpoly:ak', len: 0.88, yaw: -Math.PI / 2, build: akRifle,
    at: { optic: [0.38, 0.068], muzzle: [0.88, 0.022], mag: { x: 0.41, y: -0.02, angle: 0.38, len: 0.17, w: 0.055, d: 0.026 }, grip: [0.6, -0.016] } },
  bolt: { file: 'lowpoly:bolt', len: 1.16, yaw: -Math.PI / 2, build: boltRifle, at: { optic: [0.44, 0.057], muzzle: [1.16, 0.03] } },
};

// ---------------------------------------------------------------- Accessoires

const part = (g: THREE.Object3D, geo: THREE.BufferGeometry, color: number | THREE.Material, x: number, y: number, z = 0) => {
  const m = new THREE.Mesh(geo, typeof color === 'number' ? flat(color, { rough: 0.55, metal: 0.3 }) : color);
  m.position.set(x, y, z);
  m.castShadow = true;
  g.add(m);
  return m;
};
const tubeX = (r: number, len: number, seg = 10) => new THREE.CylinderGeometry(r, r, len, seg).rotateZ(Math.PI / 2);
const lens = (color: number) => flat(color, { emissive: color, rough: 0.2 });

// Chaque accessoire est construit autour de son point de fixation (repère de l'arme, canon vers +X)
function modMesh(id: string, mag?: MagAt): THREE.Group {
  const g = new THREE.Group();
  switch (id) {
    case 'o_reddot':
      part(g, new THREE.BoxGeometry(0.045, 0.01, 0.028), POLY, 0, 0.005);
      part(g, tubeX(0.015, 0.035), POLY, 0, 0.026);
      part(g, new THREE.CircleGeometry(0.003, 8).rotateY(-Math.PI / 2), lens(0xff3020), -0.018, 0.026);
      break;
    case 'o_holo':
      part(g, new THREE.BoxGeometry(0.07, 0.012, 0.032), POLY, 0, 0.006);
      part(g, new THREE.BoxGeometry(0.06, 0.03, 0.006), POLY, 0, 0.03, 0.016);
      part(g, new THREE.BoxGeometry(0.06, 0.03, 0.006), POLY, 0, 0.03, -0.016);
      part(g, new THREE.BoxGeometry(0.06, 0.006, 0.038), POLY, 0, 0.048);
      part(g, new THREE.PlaneGeometry(0.028, 0.026).rotateY(-Math.PI / 2), flat(0x88c8ff, { opacity: 0.35 }), 0.012, 0.03);
      break;
    case 'o_x4':
      for (const x of [-0.04, 0.04]) part(g, new THREE.BoxGeometry(0.018, 0.03, 0.026), STEEL, x, 0.015);
      part(g, tubeX(0.015, 0.2), POLY, 0, 0.04);
      part(g, tubeX(0.022, 0.05), POLY, 0.11, 0.04);
      part(g, tubeX(0.019, 0.04), POLY, -0.11, 0.04);
      part(g, new THREE.CircleGeometry(0.018, 10).rotateY(Math.PI / 2), lens(0x2a4a6a), 0.136, 0.04);
      break;
    case 'mz_supp':
      part(g, tubeX(0.019, 0.17, 10), POLY, 0.085, 0);
      part(g, tubeX(0.02, 0.012, 10), STEEL, 0.006, 0);
      break;
    case 'mz_comp':
      part(g, tubeX(0.015, 0.05, 6), STEEL, 0.025, 0);
      for (const x of [0.015, 0.03]) part(g, new THREE.BoxGeometry(0.006, 0.006, 0.032), 0x1d1f21, x, 0.012);
      break;
    case 'mz_brake':
      part(g, new THREE.BoxGeometry(0.06, 0.026, 0.04), STEEL, 0.03, 0);
      for (const x of [0.015, 0.03, 0.045]) part(g, new THREE.BoxGeometry(0.006, 0.02, 0.042), 0x1d1f21, x, 0);
      break;
    case 'mg_ext': case 'mg_drum': {
      if (!mag) break;
      const a = new THREE.Group();
      a.rotation.z = mag.angle;
      g.add(a);
      if (mag.base) part(a, new THREE.BoxGeometry(mag.w * 0.9, id === 'mg_drum' ? 0.07 : 0.035, mag.d), POLY, 0, -0.017); // rallonge sous la poignée
      else if (id === 'mg_ext') part(a, new THREE.BoxGeometry(mag.w, mag.len * 1.45, mag.d), STEEL, 0, -mag.len * 0.725);
      else {
        part(a, new THREE.BoxGeometry(mag.w * 0.8, 0.05, mag.d), STEEL, 0, -0.025);
        part(a, new THREE.CylinderGeometry(0.065, 0.065, 0.055, 12).rotateX(Math.PI / 2), 0x5d6266, 0, -0.1);
        part(a, new THREE.CylinderGeometry(0.03, 0.03, 0.058, 8).rotateX(Math.PI / 2), STEEL, 0, -0.1);
      }
      break;
    }
    case 'gp_vert':
      part(g, new THREE.BoxGeometry(0.03, 0.085, 0.028), 0x4a4e52, 0, -0.042);
      break;
    case 'gp_angle': {
      const m = part(g, new THREE.BoxGeometry(0.075, 0.035, 0.026), 0x4a4e52, 0.01, -0.016);
      m.rotation.z = -0.5;
      break;
    }
  }
  return g;
}

const SLOT_OF: Record<string, ModSlot> = Object.fromEntries(Object.values(MODS).map(m => [m.id, m.slot]));

// Arme avec ses accessoires (modèle neuf, à la taille réelle). Le chargeur d'origine est caché sous un chargeur accessoire.
export function buildWeapon(id: WeaponId, mods?: Mods): THREE.Group {
  const def = WEAPON_MODELS[id];
  const g = def.build!();
  const c = g.userData.center as THREE.Vector3;
  for (const mid of Object.values(mods ?? {})) {
    if (!mid || !modFits(id, mid)) continue;
    const slot = SLOT_OF[mid]!, at = def.at;
    let m: THREE.Group | null = null;
    if (slot === 'optic' && at?.optic) { m = modMesh(mid); m.position.set(at.optic[0], at.optic[1], 0); }
    else if (slot === 'muzzle' && at?.muzzle) { m = modMesh(mid); m.position.set(at.muzzle[0], at.muzzle[1], 0); }
    else if (slot === 'grip' && at?.grip) { m = modMesh(mid); m.position.set(at.grip[0], at.grip[1], 0); }
    else if (slot === 'mag' && at?.mag) {
      m = modMesh(mid, at.mag);
      m.position.set(at.mag.x, at.mag.y, 0);
      if (!at.mag.base) { const orig = g.getObjectByName('mag'); if (orig) orig.visible = false; }
    }
    if (m) { m.position.sub(c); g.add(m); }
  }
  return g;
}

// Copie du modèle sans les pièces en trop, tournée canon vers +Z
export function weaponObject(src: THREE.Object3D, def: WeaponModel): THREE.Object3D {
  const o = src.clone();
  if (def.drop) {
    const gone: THREE.Object3D[] = [];
    o.traverse(c => { if (c !== o && def.drop!.test(c.name)) gone.push(c); });
    for (const c of gone) c.removeFromParent();
  }
  o.rotation.y = def.yaw;
  return o;
}

// Accessoire seul (pour son icône) : posé à plat, vu de profil
export function modObject(id: string): THREE.Group {
  const mag: MagAt = { x: 0, y: 0, angle: 0, len: 0.14, w: 0.05, d: 0.024 };
  return modMesh(id, mag);
}
